<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
 <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Input Wrapper -->
      <div :class="{ 'p-input-wrapper relative': hasPrefix || hasSuffix }">
        <div class="p-input-container">
          <div v-if="hasPrefix"
            class="absolute left-3 top-1/2 -translate-y-1/2 flex items-center z-10 w-5 justify-center">
            <slot name="prefix"></slot>
          </div>
          <div v-if="hasSuffix || (clearable && isFilled) || (type === 'password' && toggleMask)"
            class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div v-if="hasSuffix">
              <slot name="suffix"></slot>
            </div>
            <div v-if="clearable && isFilled">
              <i class="pi pi-times cursor-pointer" @click="clear"></i>
            </div>
            <div v-if="type === 'password' && toggleMask">
              <i class="pi cursor-pointer" :class="[showPassword ? 'pi-eye-slash' : 'pi-eye']"
                @click="togglePasswordVisibility"></i>
            </div>
          </div>
          <InputText
            ref="inputRef"
            :id="id"
            v-model="model"
            :class="[
              'p-inputtext p-component w-full',
              {
                'p-filled': isFilled,
                'p-inputtext-sm': size === 'small',
                'p-inputtext-lg': size === 'large',
                'p-invalid': isInvalid,
                'p-disabled': disabled,
                [`p-variant-${variant}`]: variant,
                'pl-9': hasPrefix,
                'pr-9': hasSuffix || (clearable && isFilled) || (type === 'password' && toggleMask)
              }
            ]"
            :required="required"
            :disabled="disabled"
            :placeholder="placeholder"
            :type="inputType"
            :maxlength="maxLength"
            :minlength="minLength"
            :pattern="pattern"
            :autocomplete="autocomplete"
            :aria-label="props['aria-label']"
            :aria-labelledby="props['aria-labelledby']"
            :aria-invalid="!!isInvalid"
            :aria-required="required"
            :aria-describedby="ariaDescribedBy"
            :readonly="readonly"
            v-bind="$attrs"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeydown"
            @change="onChange"
          />
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-if="shouldShowError" :id="`${id}-error`" class="p-error block text-red-500">
        {{ getFormattedErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import InputText from 'primevue/inputtext'

export default defineComponent({
  name: 'MOPInputText',
  components: {
    InputText
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

export interface Props {
  modelValue?: string | number | null
  size?: 'small' | 'large'
  invalid?: boolean
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  placeholder?: string
  helperText?: string
  label?: string
  labelStyle?: 'fixed' | 'float' | 'inline'
  'aria-label'?: string
  'aria-labelledby'?: string
  id?: string
  type?: string
  autocomplete?: string
  keyfilter?: string | RegExp
  maxLength?: number
  minLength?: number
  pattern?: string
  variant?: 'outlined' | 'filled'
  clearable?: boolean
  toggleMask?: boolean
  name: string
  hasPrefix?: boolean
  hasSuffix?: boolean
  validationType?: 'email' | 'phone' | 'password' | 'url' | 'website' | 'numeric' | 'alpha' | 'alphanumeric' | 'gstin' | 'pan' | 'tan' | 'ifsc' | 'swift' | 'postalcode' | 'creditperiod' | 'positive'
  customRules?: Record<string, (value: any) => boolean | string>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  labelStyle: 'fixed',
  'aria-label': '',
  'aria-labelledby': '',
  id: () => `mop-input-${Math.random().toString(36).substr(2, 9)}`,
  type: 'text',
  autocomplete: undefined,
  keyfilter: undefined,
  maxLength: undefined,
  minLength: undefined,
  pattern: undefined,
  variant: 'outlined',
  clearable: false,
  toggleMask: false,
  hasPrefix: false,
  hasSuffix: false,
  validationType: undefined,
  customRules: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null]
  'input': [event: Event]
  'focus': [event: Event]
  'blur': [event: Event]
  'keydown': [event: Event]
  'change': [event: Event]
  'clear': []
}>()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const isFocused = ref(false)
const showPassword = ref(false)
const touched = ref(false)

// Local model for v-model binding
const model = ref<string>(props.modelValue?.toString() || '')

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  name: props.name,
  modelValue: model.value,
  required: props.required,
  validationType: props.validationType,
  minLength: props.minLength,
  maxLength: props.maxLength,
  customRules: props.customRules
})

// Watch for external modelValue changes
watch(() => props.modelValue, (newVal) => {
  const newValue = newVal?.toString() || ''
  model.value = newValue
  // Update validation state when value is set externally
  if (newValue && !touched.value) {
    updateValue(newValue)
  }
}, { immediate: true })

// Watch fieldValue changes to update model
watch(() => fieldValue.value, (newVal) => {
  model.value = newVal?.toString() || ''
}, { immediate: true })

const isFilled = computed(() => {
  return model.value != null && model.value !== ''
})

const isInvalid = computed(() => {
  return props.invalid || shouldShowError.value
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  if (shouldShowError.value) ids.push(`${props.id}-error`)
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return !!props.helperText || shouldShowError.value
})

const inputType = computed(() => {
  if (props.type === 'password' && props.toggleMask) {
    return showPassword.value ? 'text' : 'password'
  }
  return props.type
})

// Get placeholder based on validation type
const getPlaceholderByType = computed(() => {
  if (props.placeholder) return props.placeholder

  switch (props.validationType) {
    case 'email':
      return 'Enter a valid email address'
    case 'phone':
      return 'Enter a valid phone number'
    case 'password':
      return 'Min 8 chars with uppercase, lowercase, number & special char'
    case 'url':
      return 'Enter a valid URL'
    case 'website':
      return 'Enter a valid website URL'
    case 'numeric':
      return 'Enter numbers only'
    case 'alpha':
      return 'Enter letters only'
    case 'alphanumeric':
      return 'Enter letters and numbers only'
    case 'gstin':
      return 'Enter 15-character GSTIN (e.g., 22AAAAA0000A1Z5)'
    case 'pan':
      return 'Enter 10-character PAN (e.g., ABCDE1234F)'
    case 'tan':
      return 'Enter 10-character TAN (e.g., ABCD12345E)'
    case 'ifsc':
      return 'Enter 11-character IFSC code (e.g., SBIN0000123)'
    case 'swift':
      return 'Enter 8 or 11-character SWIFT code'
    case 'postalcode':
      return 'Enter valid postal code'
    case 'creditperiod':
      return 'Enter days (0-365)'
    case 'positive':
      return 'Enter positive number'
    default:
      return ''
  }
})

// Format error message based on validation type
const getFormattedErrorMessage = computed(() => {
  if (!errorMessage.value) return ''

  if (props.required && isEmpty(model.value)) {
    return `${props.name} is required`
  }

  switch (props.validationType) {
    case 'email':
      return 'Please enter a valid email address'
    case 'phone':
      return 'Please enter a valid phone number'
    case 'password':
      return 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character'
    case 'url':
      return 'Please enter a valid URL'
    case 'website':
      return 'Please enter a valid website URL'
    case 'numeric':
      return 'Please enter numbers only'
    case 'alpha':
      return 'Please enter letters only'
    case 'alphanumeric':
      return 'Please enter letters and numbers only'
    case 'gstin':
      return 'Please enter a valid 15-character GSTIN'
    case 'pan':
      return 'Please enter a valid 10-character PAN'
    case 'tan':
      return 'Please enter a valid 10-character TAN'
    case 'ifsc':
      return 'Please enter a valid 11-character IFSC code'
    case 'swift':
      return 'Please enter a valid SWIFT code (8 or 11 characters)'
    case 'postalcode':
      return 'Please enter a valid postal code'
    case 'creditperiod':
      return 'Please enter a valid credit period (0-365 days)'
    case 'positive':
      return 'Please enter a positive number'
    default:
      return errorMessage.value
  }
})

const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value

  // Auto-uppercase for specific validation types
  if (['gstin', 'pan', 'tan', 'ifsc', 'swift'].includes(props.validationType || '')) {
    value = value.toUpperCase()
    target.value = value // Update the input display
  }

  // Handle numeric input
  let emitValue: string | number | null = value
  if (props.type === 'number' && value !== '') {
    const numValue = Number(value)
    emitValue = isNaN(numValue) ? null : numValue
  }

  updateValue(value)
  emit('update:modelValue', emitValue)
  emit('input', event)

  if (shouldShowError.value) {
    await validate()
  }
}

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  isFocused.value = false
  touched.value = true
  emit('blur', event)
  await handleValidation()
}

const onKeydown = (event: Event) => {
  const keyEvent = event as KeyboardEvent

  // Always prevent Enter key from triggering form submission
  if (keyEvent.key === 'Enter') {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  emit('keydown', event)
  if (!props.keyfilter) return
  const char = keyEvent.key

  if (char.length === 1 && !keyEvent.ctrlKey && !keyEvent.altKey) {
    let regex: RegExp

    if (typeof props.keyfilter === 'string') {
      switch (props.keyfilter) {
        case 'int':
          regex = /[\d\-]/
          break
        case 'number':
          regex = /[\d\-\.]/
          break
        case 'alpha':
          regex = /[a-zA-Z]/
          break
        case 'alphanum':
          regex = /[a-zA-Z0-9]/
          break
        default:
          regex = new RegExp(props.keyfilter)
      }
    } else {
      regex = props.keyfilter
    }

    if (!regex.test(char)) {
      event.preventDefault()
    }
  }
}

const onChange = async (event: Event) => {
  emit('change', event)
  await validate()
}

const clear = () => {
  model.value = ''
  updateValue('')
  emit('update:modelValue', null)
  emit('clear')
}

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const focus = () => {
  inputRef.value?.focus()
}

// Expose methods for parent form validation
defineExpose({
  focus,
  blur: () => inputRef.value?.blur(),
  clear,
  togglePasswordVisibility,
  validate,
  errorMessage,
  isValid: computed(() => !isInvalid.value)
})
</script>

<style scoped>
.p-input-wrapper :deep(.p-inputtext) {
  padding-left: 2.5rem;
}

.p-input-wrapper :deep(.pi) {
  font-size: 1rem;
  line-height: 1;
}
</style>
